import React, { useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../../customComponents/Footer";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import "./user-feedback.css";

// Custom Select Component for Modern & Premium Dropdowns
const CustomSelect = ({ options, value, onChange, placeholder }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const selectedOption = options.find((opt) => opt.value === value);

    return (
        <div className="custom-select-container" ref={dropdownRef}>
            <div
                className={`custom-select-trigger ${isOpen ? "open" : ""} ${value ? "has-value" : ""}`}
                onClick={() => setIsOpen(!isOpen)}
            >
                <span>{selectedOption ? selectedOption.label : placeholder}</span>
                <i className="ri-arrow-down-s-line custom-select-arrow"></i>
            </div>

            {isOpen && (
                <div className="custom-select-options">
                    {options.map((opt) => (
                        <div
                            key={opt.value}
                            className={`custom-select-option ${value === opt.value ? "selected" : ""}`}
                            onClick={() => {
                                onChange(opt.value);
                                setIsOpen(false);
                            }}
                        >
                            <span>{opt.label}</span>
                            {value === opt.value && <i className="ri-check-line check-icon"></i>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const UserFeedbackPage = () => {
    const [feedbackText, setFeedbackText] = useState("");
    const [activeCategory, setActiveCategory] = useState("All Feedback");
    const [projectCategory, setProjectCategory] = useState("");
    const [feedbackType, setFeedbackType] = useState("");
    const [attachmentName, setAttachmentName] = useState("");

    // Scroll handling for Category Pills
    const pillsRef = useRef(null);
    const formRef = useRef(null);
    const fileInputRef = useRef(null);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(false);

    const checkPillsScroll = () => {
        if (!pillsRef.current) return;
        const { scrollLeft, scrollWidth, clientWidth } = pillsRef.current;
        setShowLeftArrow(scrollLeft > 2);
        setShowRightArrow(scrollWidth > clientWidth && scrollLeft < scrollWidth - clientWidth - 2);
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        checkPillsScroll();
        const timer = setTimeout(checkPillsScroll, 100);

        const currentRef = pillsRef.current;
        if (currentRef) {
            currentRef.addEventListener("scroll", checkPillsScroll);
        }
        window.addEventListener("resize", checkPillsScroll);

        let resizeObserver;
        if (currentRef && typeof ResizeObserver !== "undefined") {
            resizeObserver = new ResizeObserver(checkPillsScroll);
            resizeObserver.observe(currentRef);
        }

        return () => {
            clearTimeout(timer);
            if (currentRef) {
                currentRef.removeEventListener("scroll", checkPillsScroll);
            }
            window.removeEventListener("resize", checkPillsScroll);
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
        };
    }, []);

    const handleScrollPills = (direction) => {
        if (!pillsRef.current) return;
        const scrollAmount = direction === "left" ? -200 : 200;
        pillsRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    };

    const scrollToFeedbackForm = () => {
        if (!formRef.current) return;
        formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const handleAttachmentClick = () => {
        fileInputRef.current?.click();
    };

    const handleAttachmentChange = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        setAttachmentName(file.name);
        alertSuccessMessage(`Attachment added: ${file.name}`);
    };

    const handleSubmitFeedback = () => {
        if (!projectCategory) {
            alertErrorMessage("Please select a project category.");
            return;
        }
        if (!feedbackType) {
            alertErrorMessage("Please select a feedback type.");
            return;
        }
        if (!feedbackText.trim()) {
            alertErrorMessage("Please write your feedback before submitting.");
            return;
        }
        alertSuccessMessage("Thanks! Your feedback has been submitted.");
        setProjectCategory("");
        setFeedbackType("");
        setFeedbackText("");
        setAttachmentName("");
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const projectCategoryOptions = [
        { value: "Trading", label: "Trading" },
        { value: "UI/UX", label: "UI / UX" },
        { value: "Analytics", label: "Analytics" },
        { value: "Security", label: "Security" },
        { value: "Mobile", label: "Mobile" },
        { value: "Other", label: "Other" },
    ];

    const feedbackTypeOptions = [
        { value: "Suggestion", label: "Suggestion" },
        { value: "Bug Report", label: "Bug Report" },
        { value: "Feature Request", label: "Feature Request" },
        { value: "Improvement", label: "Improvement" },
    ];

    const categories = [
        "All Feedback",
        "Trading",
        "UI/UX",
        "Analytics",
        "Security",
        "Mobile",
        "API & Webhooks",
        "Earn & Staking",
        "Account & KYC",
        "Others"
    ];

    const rewardCards = [
        {
            icon: "/images/user-feedback-img/rewards_icon.svg",
            badgeClass: "mint",
            colorClass: "mint-color",
            amount: "$1 AGT",
            title: "Submit Feedback",
            desc: "Earn $1 AGT for every valid suggestion you submit to the platform.",
        },
        {
            icon: "/images/user-feedback-img/rewards_icon2.svg",
            badgeClass: "orange",
            colorClass: "orange-color",
            amount: "$10 AGT",
            title: "Approved Feedback",
            desc: "Earn $10 AGT when your feedback gets approved by the AGCX team.",
        },
        {
            icon: "/images/user-feedback-img/rewards_icon3.svg",
            badgeClass: "purple",
            colorClass: "purple-color",
            amount: "$100 AGT",
            title: "Implemented Ideas",
            desc: "Earn $100 AGT when your idea is implemented and launched on AGCX.",
        },
    ];

    const operateSteps = [
        {
            icon: "/images/user-feedback-img/operate_icon.svg",
            title: "Submit Suggestion",
            desc: "Share your ideas or feedback through the submission form.",
        },
        {
            icon: "/images/user-feedback-img/operate_icon2.svg",
            title: "Team Review & Voting",
            desc: "Our team and community review and vote on submitted suggestions.",
        },
        {
            icon: "/images/user-feedback-img/operate_icon3.svg",
            title: "Approval & Announcement",
            desc: "Approved suggestions are announced publicly to the community.",
        },
        {
            icon: "/images/user-feedback-img/operate_icon4.svg",
            title: "Distribute Rewards",
            desc: "Rewards are distributed to users whose suggestions are approved.",
        },
    ];

    const suggestionsList = [
        {
            avatar: "A",
            bgClass: "purple-bg",
            userName: "Alex W.",
            tag: "Trading",
            title: "Add advanced chart indicators and drawing tools",
            content:
                "It would be great to have more technical analysis tools like Fibonacci retracements, volume profile, and custom drawing tools on the AGCX chart view.",
            votes: 142,
        },
        {
            avatar: "M",
            bgClass: "yellow-bg",
            userName: "Maria S.",
            tag: "UI/UX",
            title: "Mobile app dark mode with better contrast ratios",
            content:
                "The current dark mode needs improvement in contrast for readability. Adding WCAG-compliant contrast would help many users.",
            votes: 98,
        },
        {
            avatar: "D",
            bgClass: "green-bg",
            userName: "Dave K.",
            tag: "Analytics",
            title: "Portfolio performance analytics and tax report export",
            content:
                "A built-in portfolio tracker with P&L charts and CSV export for tax purposes would be extremely useful.",
            votes: 75,
        },
    ];

    return (
        <>
            <Helmet>
                <title>Suggestions & Feedback | AGCX</title>
                <meta
                    name="description"
                    content="Share your valuable suggestions and help us build a better AGCX platform for everyone. Great ideas get rewarded."
                />
            </Helmet>

            <div className="user-feedback-page">
                <div className="feedback-container">

                    {/* ========================================== */}
                    {/* --- 1. HERO SECTION --- */}
                    {/* ========================================== */}
                    <div className="feedback-hero">
                        <div className="feedback-hero-left">
                            <h1 className="feedback-hero-title">
                                Suggestions & <br />
                                <span className="gold-text">Feedback</span>
                            </h1>
                            <p className="feedback-hero-desc">
                                Share your valuable suggestions and help us build a better AGCX platform for everyone. Great ideas get rewarded.
                            </p>
                            <button
                                type="button"
                                className="btn-feedback-form-pill"
                                onClick={scrollToFeedbackForm}
                            >
                                <i className="ri-chat-3-line"></i> Feedback Form
                            </button>
                        </div>
                        <div className="feedback-hero-right">
                            <img
                                src="/images/user-feedback-img/suggestions_bg.png"
                                alt="Suggestions & Feedback Illustration"
                            />
                        </div>
                    </div>

                    {/* ========================================== */}
                    {/* --- 2. SUBMIT FEEDBACK FORM --- */}
                    {/* ========================================== */}
                    <div className="submit-feedback-card" ref={formRef} id="feedback-form">
                        <h3>Submit Feedback</h3>

                        <div className="form-grid-row">
                            <div className="form-group">
                                <label>Project Category</label>
                                <CustomSelect
                                    options={projectCategoryOptions}
                                    value={projectCategory}
                                    onChange={(val) => setProjectCategory(val)}
                                    placeholder="Select Project Category"
                                />
                            </div>

                            <div className="form-group">
                                <label>Feedback Type</label>
                                <CustomSelect
                                    options={feedbackTypeOptions}
                                    value={feedbackType}
                                    onChange={(val) => setFeedbackType(val)}
                                    placeholder="Select Feedback Type"
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Your Feedback</label>
                            <div className="textarea-wrapper">
                                <textarea
                                    className="feedback-textarea"
                                    placeholder="Write your suggestion or feedback here..."
                                    maxLength={500}
                                    value={feedbackText}
                                    onChange={(e) => setFeedbackText(e.target.value)}
                                ></textarea>
                                <span className="char-count">{feedbackText.length} / 500</span>
                            </div>
                        </div>

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".png,.jpg,.jpeg,.pdf,.txt"
                            style={{ display: "none" }}
                            onChange={handleAttachmentChange}
                        />

                        <div className="form-actions-row">
                            <button
                                type="button"
                                className="btn-generate-attachment"
                                onClick={handleAttachmentClick}
                            >
                                <i className="ri-attachment-2"></i>{" "}
                                {attachmentName ? attachmentName : "Generate Attachment"}
                            </button>
                            <button
                                type="button"
                                className="btn-submit-feedback"
                                onClick={handleSubmitFeedback}
                            >
                                <i className="ri-send-plane-fill"></i> Submit Feedback
                            </button>
                        </div>
                    </div>

                </div>



                {/* ========================================== */}
                {/* --- 3. FEEDBACK REWARDS --- */}
                {/* ========================================== */}
                <div className="rewards-section">
                    <div className="feedback-container">
                        <h2 className="section-titleh2">Feedback Rewards</h2>
                        <div className="rewards-grid">
                            {rewardCards.map((card, idx) => (
                                <div className="reward-card" key={idx}>
                                    <div className="reward-card-top">
                                        <div className={`reward-icon-badge ${card.badgeClass}`}>
                                            <img src={card.icon} alt={card.title} />
                                        </div>
                                        <span className={`reward-amount ${card.colorClass}`}>{card.amount}</span>
                                    </div>
                                    <h4>{card.title}</h4>
                                    <p>{card.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ========================================== */}
                {/* --- 4. HOW WE OPERATE? --- */}
                {/* ========================================== */}
                <div className="operate-section">
                    <div className="feedback-container">
                        <h2 className="section-title-center">How We Operate?</h2>
                        <div className="operate-grid-wrapper">
                            {operateSteps.map((step, idx) => (
                                <React.Fragment key={idx}>
                                    <div className="operate-card">
                                        <div className="operate-icon-box">
                                            <img src={step.icon} alt={step.title} />
                                        </div>
                                        <h4>{step.title}</h4>
                                        <p>{step.desc}</p>
                                    </div>
                                    {idx < operateSteps.length - 1 && (
                                        <div className="operate-arrow">
                                            <i className="ri-arrow-right-s-line"></i>
                                        </div>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ========================================== */}
                {/* --- 5. HIGH-QUALITY SUGGESTIONS & SIDEBAR --- */}
                {/* ========================================== */}

                <div className="feedback-container">
                    <div className="suggestions-layout">

                        <div className="suggestions-left-col">
                            <h2 className="section-titleh2">High-quality Suggestions</h2>

                            {/* Category Filter Pills */}
                            <div className="category-pills-wrapper">
                                {showLeftArrow && (
                                    <button
                                        type="button"
                                        className="pills-scroll-btn left"
                                        onClick={() => handleScrollPills("left")}
                                        aria-label="Scroll Left"
                                    >
                                        <i className="ri-arrow-left-s-line"></i>
                                    </button>
                                )}

                                <div className="category-pills" ref={pillsRef}>
                                    {categories.map((cat, idx) => (
                                        <button
                                            key={idx}
                                            className={`pill-btn ${activeCategory === cat ? "active" : ""}`}
                                            onClick={() => setActiveCategory(cat)}
                                        >
                                            {cat}
                                        </button>
                                    ))}
                                </div>

                                {showRightArrow && (
                                    <button
                                        type="button"
                                        className="pills-scroll-btn right"
                                        onClick={() => handleScrollPills("right")}
                                        aria-label="Scroll Right"
                                    >
                                        <i className="ri-arrow-right-s-line"></i>
                                    </button>
                                )}
                            </div>

                            {/* Suggestion List */}
                            <div className="suggestion-list">
                                {suggestionsList.map((item, idx) => (
                                    <div className="suggestion-card" key={idx}>
                                        <div className="suggestion-card-header">
                                            <div className={`user-avatar ${item.bgClass}`}>{item.avatar}</div>
                                            <span className="user-name">{item.userName}</span>
                                            <span className="tag-badge trading">{item.tag}</span>
                                        </div>
                                        <h3 className="suggestion-title">{item.title}</h3>
                                        <p className="suggestion-content">{item.content}</p>
                                        <div className="suggestion-footer">
                                            <span className="action-item active">
                                                <i className="ri-thumb-up-line"></i> {item.votes} votes
                                            </span>
                                            <span className="action-item">
                                                <i className="ri-chat-3-line"></i> Comment
                                            </span>
                                            <span className="action-item">
                                                <i className="ri-share-forward-line"></i> View
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Sidebar Widget */}
                        <div className="suggestions-right-sidebar">
                            <div className="sidebar-cta-card">
                                <img
                                    src="/images/user-feedback-img/feedback_cta_img.png"
                                    alt="Submit Other Feedback"
                                    className="sidebar-cta-img"
                                />
                                <h3>Submit Other Feedback</h3>
                                <p>
                                    Have a different type of feedback or a bug to report? Use the general feedback form to reach our team directly.
                                </p>
                                <button
                                    type="button"
                                    className="btn-send-feedback-cta"
                                    onClick={scrollToFeedbackForm}
                                >
                                    <i className="ri-send-plane-line"></i> Send Feedback
                                </button>
                                <p className="response-time-note">
                                    Average response time: <strong>24 hours</strong>
                                </p>
                            </div>
                        </div>
                    </div>

                </div>

            </div>

            <Footer />
        </>
    );
};

export default UserFeedbackPage;
